import { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getStudentFee } from '../../redux/feeRelated/feeHandle';
import {
    Box, Container, Typography, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Chip, Grid, Avatar,
    Fade, Divider, IconButton, CircularProgress, Stack
} from '@mui/material';
import {
    AccountBalanceWallet as WalletIcon,
    ReceiptLong as ReceiptIcon,
    History as HistoryIcon,
    Payment as PaymentIcon,
    Warning as WarningIcon,
    CheckCircle as PaidIcon,
    Error as UnpaidIcon,
    Pending as PartialIcon,
    CalendarMonth as CalendarIcon,
    CurrencyRupee as CurrencyIcon
} from '@mui/icons-material';
import { formatDatePart } from '../../utils/customDate';
import styled, { keyframes } from 'styled-components';

// Animations
const fadeIn = keyframes`from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); }`;

// Styled Components
const PageWrapper = styled(Box)`
    background-color: #f8fafc;
    min-height: 100vh;
    padding-bottom: 80px;
    animation: ${fadeIn} 0.5s ease-out;
`;

const PremiumHeader = styled(Box)`
    background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
    padding: 40px 0 60px;
    color: white;
    position: relative;
    overflow: hidden;
    
    &::after {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        width: 600px;
        height: 600px;
        background: radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, transparent 70%);
        border-radius: 50%;
        transform: translate(30%, -30%);
    }
`;

const ContentContainer = styled(Container)`
    margin-top: -60px;
    position: relative;
    z-index: 2;
`;

const GlassCard = styled(Paper)`
    background: rgba(255, 255, 255, 0.95) !important;
    backdrop-filter: blur(20px) !important;
    border-radius: 24px !important;
    border: 1px solid rgba(255, 255, 255, 0.8) !important;
    box-shadow: 0 10px 40px -10px rgba(0, 0, 0, 0.05) !important;
    overflow: hidden;
`;

const StatCard = styled(Paper)`
    padding: 24px;
    border-radius: 20px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    background: white;
    border: 1px solid #eef2ff;
    transition: all 0.3s ease;
    
    &:hover {
        transform: translateY(-4px);
        box-shadow: 0 12px 24px -10px rgba(99, 102, 241, 0.15);
    }
`;

const StyledTableCell = styled(TableCell)`
    font-weight: 700 !important;
    color: #475569 !important;
    background-color: #f8fafc !important;
    border-bottom: 2px solid #e2e8f0 !important;
    text-transform: uppercase;
    font-size: 0.75rem !important;
    letter-spacing: 0.05em;
`;

const ViewFee = () => {
    const dispatch = useDispatch();
    const { currentUser } = useSelector((state) => state.user);
    const { feesList, loading, error } = useSelector((state) => state.fee);
    const [feeData, setFeeData] = useState(null);

    useEffect(() => {
        if (currentUser) {
            dispatch(getStudentFee(currentUser._id));
        }
    }, [dispatch, currentUser]);

    useEffect(() => {
        if (feesList && Array.isArray(feesList) && feesList.length > 0) {
            setFeeData(feesList[0]);
        }
    }, [feesList]);

    const getStatusChip = (status) => {
        switch (status) {
            case 'Paid':
                return <Chip icon={<PaidIcon />} label="Paid" sx={{ bgcolor: '#ecfdf5', color: '#059669', fontWeight: 800, px: 1 }} />;
            case 'Unpaid':
                return <Chip icon={<UnpaidIcon />} label="Unpaid" sx={{ bgcolor: '#fef2f2', color: '#dc2626', fontWeight: 800, px: 1 }} />;
            case 'Partial':
                return <Chip icon={<PartialIcon />} label="Partial" sx={{ bgcolor: '#fffbeb', color: '#d97706', fontWeight: 800, px: 1 }} />;
            default:
                return <Chip label={status || 'Pending'} variant="outlined" />;
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Not Paid';
        return formatDatePart(dateString);
    };

    const summary = useMemo(() => {
        if (!feeData || !feeData.feeDetails) return { total: 0, paid: 0, unpaid: 0 };

        let total = 0;
        let paid = 0;
        let unpaid = 0;

        feeData.feeDetails.forEach(fee => {
            total += fee.amount || 0;
            if (fee.status === 'Paid') {
                paid += fee.amount || 0;
            } else if (fee.status === 'Partial') {
                paid += fee.paidAmount || (fee.amount / 2);
                unpaid += fee.amount - (fee.paidAmount || (fee.amount / 2));
            } else {
                unpaid += fee.amount || 0;
            }
        });

        return { total, paid, unpaid };
    }, [feeData]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', height: '80vh', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress size={50} thickness={4} />
            </Box>
        );
    }

    return (
        <PageWrapper>
            <PremiumHeader>
                <Container maxWidth="lg">
                    <Fade in timeout={800}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 64, height: 64 }}>
                                <WalletIcon sx={{ fontSize: 32 }} />
                            </Avatar>
                            <Box>
                                <Typography variant="h3" sx={{ fontWeight: 900, letterSpacing: '-0.02em' }}>
                                    My Fees
                                </Typography>
                                <Typography variant="h6" sx={{ opacity: 0.8, fontWeight: 400 }}>
                                    Review your payment history and outstanding balance.
                                </Typography>
                            </Box>
                        </Box>
                    </Fade>
                </Container>
            </PremiumHeader>

            <ContentContainer maxWidth="lg" sx={{ marginTop: '2rem' }}>
                <Grid container spacing={3}>
                    {/* Summary Cards */}
                    <Grid item xs={12} md={4}>
                        <StatCard elevation={0}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ bgcolor: '#eff6ff', color: '#3b82f6' }}><CurrencyIcon /></Avatar>
                                <Box>
                                    <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 700 }}>TOTAL ASSIGNED</Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 900 }}>Rs. {summary.total.toLocaleString()}</Typography>
                                </Box>
                            </Box>
                        </StatCard>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <StatCard elevation={0}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ bgcolor: '#f0fdf4', color: '#10b981' }}><PaidIcon /></Avatar>
                                <Box>
                                    <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 700 }}>TOTAL PAID</Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 900 }}>Rs. {summary.paid.toLocaleString()}</Typography>
                                </Box>
                            </Box>
                        </StatCard>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <StatCard elevation={0}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ bgcolor: '#fef2f2', color: '#ef4444' }}><WarningIcon /></Avatar>
                                <Box>
                                    <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 700 }}>PENDING BALANCE</Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 900, color: summary.unpaid > 0 ? '#dc2626' : 'inherit' }}>
                                        Rs. {summary.unpaid.toLocaleString()}
                                    </Typography>
                                </Box>
                            </Box>
                        </StatCard>
                    </Grid>

                    {/* Pending Alert */}
                    {summary.unpaid > 0 && (
                        <Grid item xs={12}>
                            <Fade in>
                                <Paper sx={{ 
                                    p: 3, 
                                    bgcolor: '#fff7ed', 
                                    border: '1px solid #ffedd5', 
                                    borderRadius: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 3
                                }}>
                                    <Avatar sx={{ bgcolor: '#f97316', color: 'white' }}><WarningIcon /></Avatar>
                                    <Box>
                                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#9a3412' }}>Action Required: Outstanding Balance</Typography>
                                        <Typography variant="body2" sx={{ color: '#c2410c' }}>
                                            You have a pending balance of <strong>Rs. {summary.unpaid.toLocaleString()}</strong>. 
                                            Please visit the school administration to clear your dues and avoid late fees.
                                        </Typography>
                                    </Box>
                                </Paper>
                            </Fade>
                        </Grid>
                    )}

                    {/* Detailed Fee Table */}
                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3, mt: 2 }}>
                            <Avatar sx={{ bgcolor: '#f5f3ff', color: '#8b5cf6' }}><ReceiptIcon /></Avatar>
                            <Typography variant="h5" sx={{ fontWeight: 800 }}>Detailed Fee Breakdown</Typography>
                        </Box>
                        <GlassCard>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <StyledTableCell>Particulars</StyledTableCell>
                                            <StyledTableCell align="right">Amount</StyledTableCell>
                                             <StyledTableCell>Payment Date</StyledTableCell>
                                            <StyledTableCell align="center">Status</StyledTableCell>
                                            <StyledTableCell>Remarks</StyledTableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {(!feeData || !feeData.feeDetails || feeData.feeDetails.length === 0) ? (
                                            <TableRow>
                                                <TableCell colSpan={6} align="center" sx={{ py: 10 }}>
                                                    <Stack alignItems="center" spacing={2}>
                                                        <HistoryIcon sx={{ fontSize: 48, color: '#cbd5e1' }} />
                                                        <Typography color="textSecondary" sx={{ fontWeight: 600 }}>No fee records found for this student.</Typography>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            feeData.feeDetails.map((fee, index) => (
                                                <TableRow key={index} hover sx={{ '&:last-child td': { border: 0 } }}>
                                                    <TableCell sx={{ fontWeight: 700, color: '#1e293b' }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                            <CalendarIcon fontSize="small" sx={{ color: '#64748b' }} />
                                                            {fee.month}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 800, color: '#1e293b' }}>
                                                        Rs. {fee.amount?.toLocaleString()}
                                                    </TableCell>
                                                    <TableCell color="textSecondary">
                                                        {formatDate(fee.paymentDate)}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        {getStatusChip(fee.status)}
                                                    </TableCell>
                                                      <TableCell sx={{ color: '#64748b', fontStyle: 'italic', maxWidth: 200 }}>
                                                          {(() => {
                                                              let desc = fee.description || '';
                                                              const dues = Number(fee.duesAmount) || 0;
                                                              const disc = Number(fee.discount) || 0;
                                                              if (dues > 0) {
                                                                  const duesText = `Dues: Rs. ${dues}`;
                                                                  if (!desc.toLowerCase().includes('dues') || !desc.includes(String(dues))) {
                                                                      desc = desc ? `${desc}, - ${duesText}` : duesText;
                                                                  }
                                                              }
                                                              if (disc > 0) {
                                                                  const discText = `Discount: Rs. ${disc}`;
                                                                  if (!desc.toLowerCase().includes('discount') || !desc.includes(String(disc))) {
                                                                      desc = desc ? `${desc}, - ${discText}` : discText;
                                                                  }
                                                              }
                                                              return desc || '-';
                                                          })()}
                                                      </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </GlassCard>
                    </Grid>
                </Grid>
            </ContentContainer>
        </PageWrapper>
    );
};

export default ViewFee;
